import React, {useEffect, useRef, useState} from 'react'
import {
    Box, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField
} from '@mui/material'
import Typography from '@mui/material/Typography'
import SaveIcon from '@mui/icons-material/Save'
import UploadIcon from '@mui/icons-material/Upload'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import {LoaderFunctionArgs, useLoaderData, useNavigate, useParams} from 'react-router-dom'
import axios from 'axios'
import {CampaignItem} from './ItemList'
import {LoreScrollPrint} from './components/LoreScrollPrint'

interface EventOption {
    id: number
    name: string
}

export const itemLoader = async ({params}: LoaderFunctionArgs): Promise<CampaignItem | null> => {
    if (!params.id) return null
    return axios.get(`/items/items/${params.id}`).then(({data}) => data)
}

const ITEM_STATES = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED'] as const
const STATE_LABELS: Record<string, string> = {
    DRAFT: 'Draft',
    SUBMITTED: 'Submitted',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
}

const EMPTY_ITEM: CampaignItem = {
    id: 0,
    name: '',
    item_code: '',
    magic_item_code: '',
    image_url: null,
    lore: '',
    state: 'DRAFT',
    eventId: null,
    dropped_at: null,
}

export function ItemViewer() {
    const params = useParams()
    const loaded = useLoaderData() as CampaignItem | null
    const isNew = params.id === undefined
    const navigate = useNavigate()

    const [item, setItem] = useState<CampaignItem>(loaded ?? EMPTY_ITEM)
    const [events, setEvents] = useState<EventOption[]>([])
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [imageUploading, setImageUploading] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        axios.get('/plot/events').then(({data}) => setEvents(data)).catch(() => {})
    }, [])

    function set(field: keyof CampaignItem, value: any) {
        setItem(prev => ({...prev, [field]: value}))
    }

    async function save() {
        if (!item.name.trim()) return setError('Name is required.')
        if (!item.item_code?.trim() && !item.magic_item_code?.trim()) return setError('At least one of item code or magic item code is required.')
        if (item.item_code?.trim() && !item.item_code.startsWith('AMA-')) return setError('Item code must start with AMA-.')
        setError(null)
        setSaving(true)
        try {
            if (isNew) {
                const {data} = await axios.post('/items/items', item)
                navigate(`/items/${data.id}`, {replace: true})
            } else {
                const {data} = await axios.put(`/items/items/${item.id}`, item)
                setItem(data)
            }
        } catch (e: any) {
            setError(e.response?.data?.error ?? 'Save failed.')
        } finally {
            setSaving(false)
        }
    }

    function printLore() {
        const win = window.open('', '_blank', 'width=1100,height=850')
        if (!win) return
        const html = document.createElement('html')
        const head = document.createElement('head')
        head.innerHTML = `<style>
            body { margin: 0; padding: 0; background: white; }
            * { box-sizing: border-box; }
        </style>`
        const body = document.createElement('body')
        const root = document.createElement('div')
        body.appendChild(root)
        html.appendChild(head)
        html.appendChild(body)
        win.document.replaceChild(html, win.document.documentElement)
        import('react-dom/client').then(({createRoot}) => {
            const reactRoot = createRoot(root)
            reactRoot.render(
                <LoreScrollPrint
                    name={item.name}
                    lore={item.lore ?? ''}
                    onReady={(size) => {
                        const pageStyle = root.ownerDocument.createElement('style')
                        pageStyle.textContent = size === 'small'
                            ? '@page { size: 11in 8.5in landscape; margin: 0; }'
                            : '@page { size: 11in 8.5in landscape; margin: 0; }'
                        root.ownerDocument.head.appendChild(pageStyle)
                        setTimeout(() => { win.focus(); win.print() }, 100)
                    }}
                />
            )
        })
    }

    async function uploadImage(file: File) {
        if (!item.id) return
        setImageUploading(true)
        const form = new FormData()
        form.append('image', file)
        try {
            const {data} = await axios.post(`/items/items/${item.id}/image`, form)
            setItem(prev => ({...prev, image_url: data.image_url}))
        } catch {
            setError('Image upload failed.')
        } finally {
            setImageUploading(false)
        }
    }

    return (
        <Paper elevation={2} sx={{p: 3}}>
            <Typography variant="h5" gutterBottom>
                {isNew ? 'New Campaign Item' : item.name || 'Campaign Item'}
            </Typography>

            {error && (
                <Typography color="error" sx={{mb: 2}}>{error}</Typography>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Name"
                        fullWidth
                        required
                        value={item.name}
                        onChange={e => set('name', e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Item Code"
                        fullWidth
                        placeholder="AMA-"
                        value={item.item_code ?? ''}
                        onChange={e => set('item_code', e.target.value || null)}
                        helperText="If provided, must start with AMA-"
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Magic Item Code"
                        fullWidth
                        value={item.magic_item_code ?? ''}
                        onChange={e => set('magic_item_code', e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                        <InputLabel>State</InputLabel>
                        <Select
                            value={item.state}
                            label="State"
                            onChange={e => set('state', e.target.value)}
                        >
                            {ITEM_STATES.map(s => (
                                <MenuItem key={s} value={s}>{STATE_LABELS[s]}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                    <FormControl fullWidth>
                        <InputLabel>Dropped At</InputLabel>
                        <Select
                            value={item.eventId ?? ''}
                            label="Dropped At"
                            onChange={e => set('eventId', e.target.value === '' ? null : e.target.value)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {events.map(ev => (
                                <MenuItem key={ev.id} value={ev.id}>{ev.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        label="Lore"
                        fullWidth
                        multiline
                        rows={6}
                        value={item.lore ?? ''}
                        onChange={e => set('lore', e.target.value)}
                    />
                </Grid>

                {!isNew && (
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Image</Typography>
                        {item.image_url && (
                            <Box sx={{mb: 1}}>
                                <img src={item.image_url} alt="Item" style={{maxHeight: 200, maxWidth: '100%', borderRadius: 4}}/>
                            </Box>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            ref={fileInputRef}
                            style={{display: 'none'}}
                            onChange={e => {
                                const file = e.target.files?.[0]
                                if (file) uploadImage(file)
                            }}
                        />
                        <Button
                            variant="outlined"
                            startIcon={<UploadIcon/>}
                            onClick={() => fileInputRef.current?.click()}
                            disabled={imageUploading}
                        >
                            {imageUploading ? 'Uploading…' : 'Upload Image'}
                        </Button>
                    </Grid>
                )}

                <Grid item xs={12} sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                    <Button
                        variant="contained"
                        startIcon={<SaveIcon/>}
                        onClick={save}
                        disabled={saving}
                    >
                        {saving ? 'Saving…' : isNew ? 'Create Item' : 'Save Changes'}
                    </Button>
                    {!isNew && (
                        <Button
                            variant="outlined"
                            startIcon={<AutoStoriesIcon/>}
                            onClick={printLore}
                            disabled={!item.lore?.trim()}
                        >
                            Print Lore Scroll
                        </Button>
                    )}
                </Grid>
            </Grid>
        </Paper>
    )
}
